// Library imports
import { useEffect, useRef } from 'react';
import Feature from 'ol/Feature';
import Overlay from 'ol/Overlay';
import KML from 'ol/format/KML';
import { observer } from 'mobx-react';
import { Polygon } from 'ol/geom';
import OlMap from 'ol/Map';

// Local imports
import { DrawInteractionTool } from '../tool-base';
import { calcArea, boundsToGeoJson, searchToGeoJson, findSimilar } from '../tool-utils';
import { addLoadingIcon, removeLoadingIcon, buildVolumeLabel, buildAreaLabel } from '../tool-labels';
import { layerStore } from './utils/store/layer-store';
import { measurementStore } from './utils/store/measurement-store';
import { processingStore } from './utils/store/processing-store';
import { CalcSurface, Measurement } from '../../../../typings/reality-capture/volumes/models';
import { getComparisonVolume, getStockpileVolume } from './utils/get-volume';
import { determineTargetArea, setHeatmap, splitOpenlayersFeatureIntoSegments, vizSegments } from './utils/helpers/helpers';
import { ToolsDebug } from '../tool-utils';

export class VolumeTool extends DrawInteractionTool {
    public sourceProj = this.mapWrapper.getProjection();
    public map = this.mapWrapper.getInstance() as OlMap;
    private lastClickCoords: Coordinate[] = [];
    pendingFeatures: Feature[] = [];
    // The corridor feature generated by the alignment panel; parameter changes
    // recalculate its measurement in place instead of adding a new one.
    activeAlignmentFeature: Feature | null = null;

    onDrawStart(e) {
        const currFeature = e.feature;
        const [evt, label] = this.bindEvents(currFeature);
        this.inProgressLabel = label;
        this.geometryListener = evt;
        document.querySelectorAll('.measure-label').forEach(el => (el.parentElement.style.pointerEvents = 'none'));
        super.onDrawStart(e);
    }

    async onDrawEnd(e) {
        const currFeature = e.feature;
        this.pendingFeatures.push(currFeature);
        layerStore.incrementDrawCount();
        super.onDrawEnd(e);
    }

    async calculatePendingVolume() {
        const features = this.pendingFeatures.splice(0);
        await Promise.all(features.map(f => this.addVolume(f)));
    }

    public VisibleMeasurementsSync = observer(() => {
        const visibleMeasurements = measurementStore.listVisibleMeasurements();
        const shownMeasurementsByIdRef = useRef<Map<number, Measurement>>(new Map());

        useEffect(() => {
            const shownMeasurementsById = shownMeasurementsByIdRef.current;
            const nextVisibleIds = new Set(visibleMeasurements.map(m => m.id));

            for (const measurement of visibleMeasurements) {
                const isNewlyVisible = !shownMeasurementsById.has(measurement.id);
                shownMeasurementsById.set(measurement.id, measurement);
                if (isNewlyVisible) {
                    this.showMeasurement(measurement);
                }
            }

            for (const [id, measurement] of Array.from(shownMeasurementsById.entries())) {
                if (!nextVisibleIds.has(id)) {
                    this.clearMeasurement(measurement.feature);
                    shownMeasurementsById.delete(id);
                }
            }
        }, [visibleMeasurements]);
        return null;
    });

    buildLabelText = (measurement: Measurement) => {
        let label = '';

        if (measurement.name) {
            label += buildVolumeLabel(measurement.name, measurement.fill_volume, measurement.cut_volume);
        } else {
            label += buildVolumeLabel(measurement.id, measurement.fill_volume, measurement.cut_volume);
        }

        label += `<br> ${__('js_map_measuring_tools.dem_area')}: ${buildAreaLabel(measurement.area)}`;

        if (measurement.failure_rate && measurement.failure_rate > 0.01) {
            const pct = (measurement.failure_rate * 100).toFixed(1);
            const tip = `${__('js_map_measuring_tools.ignored_areas')}: ${pct}%`;
            label += `<br><span style="display:inline-block;position:relative;margin-top:3px"` +
                ` onmouseenter="this.lastElementChild.style.display='block'"` +
                ` onmouseleave="this.lastElementChild.style.display='none'">` +
                `<span style="display:inline-flex;align-items:center;justify-content:center;` +
                `width:16px;height:16px;border-radius:50%;background:#fef3c7;color:#d97706;` +
                `font-size:10px;font-weight:bold;border:1px solid #fde68a;cursor:pointer;box-sizing:border-box">i</span>` +
                `<span style="display:none;position:absolute;bottom:calc(100% + 5px);left:50%;` +
                `transform:translateX(-50%);white-space:nowrap;background:#fffbeb;` +
                `border:1px solid #fde68a;border-radius:4px;padding:3px 8px;` +
                `font-size:11px;color:#92400e;z-index:9999;box-shadow:0 2px 4px rgba(0,0,0,.2)">` +
                `⚠ ${tip}</span></span>`;
        }

        return label;
    };

    updateLabel = (feature: Feature, labelText: string) => {
        let label = feature.get('label') as Overlay | null;
        if (!label) {
            label = this.createOverlayLabel(feature);
            feature.set('label', label);
            this.mapWrapper.getInstance().addOverlay(label);
        } else if (typeof (label as any).getMap === 'function' && !(label as any).getMap()) {
            this.mapWrapper.getInstance().addOverlay(label);
        }

        // OL puts stopEvent:false overlays in ol-overlaycontainer (no pointer-events).
        // Override both the element and its container so hover effects work.
        const el = label.getElement();
        el.style.pointerEvents = 'auto';
        if (el.parentElement) el.parentElement.style.pointerEvents = 'auto';

        el.innerHTML = labelText;

        const geom = feature.getGeometry() as Polygon;
        label.setPosition(geom.getInteriorPoint().getCoordinates());
    };

    setMeasurementVisible = (measurement: Measurement, show: boolean) => {
        measurementStore.setVolumeVisible(measurement.uuid, show);
    };

    showMeasurement = (measurement: Measurement) => {
        setHeatmap(this.mapWrapper, measurement.feature, measurement.viz_surface.heatmap_url);

        if (ToolsDebug.enabled) {
            const targetSegmentArea = determineTargetArea(measurement.calcTarget.surfaceType, measurement.calcReference.surfaceType);
            const splitSegments = splitOpenlayersFeatureIntoSegments(measurement.feature, targetSegmentArea);
            vizSegments(splitSegments, this.mapWrapper, measurement.feature);
        }

        const source = this.drawLayer.getSource();
        if (!source.hasFeature(measurement.feature)) {
            source.addFeature(measurement.feature);
        }

        const labelText = this.buildLabelText(measurement);
        this.updateLabel(measurement.feature, labelText);
    };

    clearMeasurement = (feature: Feature) => {
        const label = feature.get('label') as Overlay | null;
        if (label && typeof (label as any).getMap === 'function' && (label as any).getMap()) {
            this.mapWrapper.getInstance().removeOverlay(label);
        }
        feature.set('label', null);

        const heatmapLayer = feature.get('heatmapLayer');
        if (heatmapLayer) {
            this.mapWrapper.getInstance().removeLayer(heatmapLayer);
            feature.set('heatmapLayer', null);
        }

        const source = this.drawLayer.getSource();
        if (source.hasFeature(feature)) {
            source.removeFeature(feature);
        }
        this.onDrawRemove?.(feature);
    };

    raiseError = async (feature: Feature, error: unknown) => {
        feature.set('error', error);
        const geom = feature.getGeometry() as Polygon;
        const { area, prefix } = calcArea(geom, this.sourceProj);
        let labelText = buildAreaLabel(area);
        labelText += `<span style="color: red;">${error}</span>`;
        this.updateLabel(feature, labelText);
        removeLoadingIcon(this.mapWrapper, feature);
    };

    removeAllLoadingIcons = () => {
        const features = this.drawLayer.getSource().getFeatures();
        features.forEach(feature => {
            removeLoadingIcon(this.mapWrapper, feature);
        });
    };

    calcVolume = async (feature: Feature, customBaseHeight: number = null, tolerance = 0.1, calcReference: CalcSurface = null, calcTarget: CalcSurface = null) => {
        console.log('calcVolume', feature, customBaseHeight, tolerance, calcReference, calcTarget);

        const cacheUuid: string = processingStore.start();
        const overlay = feature.get('loadingOverlay');
        if (!overlay) {
            addLoadingIcon(this.mapWrapper, feature, cacheUuid);
        }

        let measurement: Measurement;
        if (calcTarget) {
            const params = { sourceProj: this.sourceProj, feature: feature, tolerance: tolerance, cacheUuid: cacheUuid, calcReference, calcTarget };
            measurement = await getComparisonVolume(params);
        } else {
            if (!calcReference) {
                await this.raiseError(feature, __('js_map_measuring_tools.error_no_dem'));
                return;
            }
            const params = { sourceProj: this.sourceProj, feature: feature, customBaseHeight: customBaseHeight, tolerance: tolerance, cacheUuid: cacheUuid, calcReference };
            measurement = await getStockpileVolume(params);
        }

        if (!measurement) {
            await this.raiseError(feature, __('js_map_measuring_tools.http_error'));
            return;
        }

        removeLoadingIcon(this.mapWrapper, measurement.feature);
        return measurement;
    };

    addVolume = async (feature: Feature) => {
        const calcTarget = layerStore.target;
        const calcReference = layerStore.reference;
        console.log('addVolume', feature, calcReference, calcTarget);

        const measurement: Measurement = await this.calcVolume(feature, null, 0.1, calcReference, calcTarget);
        const id = measurementStore.addMeasurement(measurement);
        measurement.id = id;
        feature.set('measurementId', id);
    };

    updateVolume = async (measurement: Measurement) => {
        console.log('updateVolume', measurement);
        const NewMeasurement = await this.calcVolume(measurement.feature, measurement.baseHeight, measurement.tolerance, measurement.calcReference, measurement.calcTarget);
        if (!NewMeasurement) {
            return;
        }
        NewMeasurement.id = measurement.id;
        NewMeasurement.uuid = measurement.uuid;
        if (measurement.name) {
            NewMeasurement.name = measurement.name;
        }
        measurementStore.updateMeasurement(NewMeasurement);
        this.showMeasurement(NewMeasurement);
    };

    replaceVolume = async (oldFeature: Feature, newFeature: Feature): Promise<boolean> => {
        const measurementId = oldFeature.get('measurementId');
        if (!measurementId) return false;
        const measurement = measurementStore.measurements.find(m => m.id === measurementId);
        if (!measurement) return false;

        this.clearMeasurement(oldFeature);
        // Claim the id before awaiting so rapid successive edits chain onto the same measurement
        newFeature.set('measurementId', measurement.id);

        const calcReference = layerStore.reference ?? measurement.calcReference;
        const calcTarget = layerStore.target ?? measurement.calcTarget;
        const newMeasurement = await this.calcVolume(newFeature, measurement.baseHeight, measurement.tolerance, calcReference, calcTarget);
        if (!newMeasurement) {
            return true;
        }
        newMeasurement.id = measurement.id;
        newMeasurement.uuid = measurement.uuid;
        if (measurement.name) {
            newMeasurement.name = measurement.name;
        }
        measurementStore.updateMeasurement(newMeasurement);
        this.showMeasurement(newMeasurement);
        return true;
    };

    generateDrawingFromKml = async (kmlFile: Blob) => {
        const kmlFormat = new KML();
        const fileContent = await kmlFile.text();
        const features = kmlFormat.readFeatures(fileContent, {
            featureProjection: this.sourceProj,
        });

        features.forEach(async feature => {
            if (feature.getGeometry().getType() === 'Polygon') {
                feature.set('type', 'volume-measurement');
                const label = this.createOverlayLabel(feature);
                feature.set('label', label);
                this.drawLayer.getSource().addFeature(feature);
                const geom = feature.getGeometry() as Polygon;
                const { area, prefix } = calcArea(geom, this.sourceProj);
                this.updateLabel(feature, buildAreaLabel(area, prefix));
                this.bindEvents(feature);
                this.addVolume(feature);
            }
        });
    };

    addFeature(feature: Feature, showLabel: boolean = true) {
        feature.set('finished', true);
        this.bindEvents(feature);
        this.drawLayer.getSource().addFeature(feature);
    }

    restoreFromCoords = (coords: Coordinate[][][], sourceUuid?: string): Feature[] => {
        if (!Array.isArray(coords)) return;
        return coords.map(outline => {
            const feature = new Feature({
                geometry: new Polygon(outline),
            });
            feature.set('finished', true);
            this.drawLayer.getSource().addFeature(feature);
            this.bindEvents(feature);
            feature.getGeometry().dispatchEvent('change');
            feature.set('sourceUuid', sourceUuid);
            return feature;
        });
    };

    removeMeasurement = (feature: Feature) => {
        if (feature === this.activeAlignmentFeature) {
            this.activeAlignmentFeature = null;
        }
        const measurementId = feature.get('measurementId');
        if (measurementId) {
            const measurement = measurementStore.measurements.find(m => m.id === measurementId);
            if (measurement) {
                measurementStore.deleteMeasurement(measurement.uuid);
            }
        }
        
        const pendingIdx = this.pendingFeatures.indexOf(feature);
        if (pendingIdx !== -1) {
            this.pendingFeatures.splice(pendingIdx, 1);
        }

        this.clearMeasurement(feature);
    };

    clearAll = () => {
        const features = this.drawLayer.getSource().getFeatures();
        features.forEach(feature => {
            this.clearMeasurement(feature);
        });
        measurementStore.clearMeasurements();
        this.removeAllLoadingIcons();
        this.drawLayer.getSource().clear();
        this.pendingFeatures = [];
        this.activeAlignmentFeature = null;
        layerStore.resetDrawCount();
    };

    updateFromResize = (feature: Feature) => {
        const calculatedArea = feature.get('measuredArea');
        const geom = feature.getGeometry() as Polygon;
        const currArea = calcArea(geom, this.sourceProj);
        if (calculatedArea !== currArea) {
            const measurementId = feature.get('measurementId');
            const measurement = measurementStore.measurements.find(m => m.id === measurementId);
            if (measurement) {
                this.updateVolume(measurement);
            }
        }
    };

    bindEvents = (feature: Feature) => {
        const label = this.createOverlayLabel(feature);
        this.mapWrapper.getInstance().addOverlay(label);
        let changeTimeout: ReturnType<typeof setTimeout> | undefined;

        const evt = feature.getGeometry().on('change', () => {
            if (changeTimeout) {
                clearTimeout(changeTimeout);
            }
            const geom = feature.getGeometry() as Polygon;
            const { area, prefix } = calcArea(geom, this.sourceProj);
            const coords = geom.getCoordinates();
            if (this.lastClickCoords.length !== coords[0].length) {
                this.onDrawProgressCallback?.(feature);
            }
            this.lastClickCoords = coords[0] as Coordinate[];
            const labelText = buildAreaLabel(area);
            this.updateLabel(feature, labelText);

            changeTimeout = setTimeout(() => {
                // Wait for 1 second before fetching new volume
                this.updateFromResize(feature);
            }, 1000);
        });

        feature.set('type', 'volume-measurement');
        feature.set('label', label);

        return [evt, label] as const;
    };

    aiZoom = async (feature: Feature) => {   
        await boundsToGeoJson(this, feature);
    };

    findSimilar = async (feature: Feature) => {
        await findSimilar(this, feature);
    };
}
